/* eslint-disable max-lines-per-function, max-statements, no-undef */

import React, { useLayoutEffect, useRef, useState } from 'react';

export interface ActionAttentionState {
  activeDecision: boolean;
  planningRunning: boolean;
  hidden?: boolean;
  isReadOnly?: boolean;
  wasContinued?: boolean;
  actionButtonCount: number;
}

export const shouldShowActionAttention = ({
  activeDecision,
  planningRunning,
  hidden,
  isReadOnly,
  wasContinued,
  actionButtonCount,
}: ActionAttentionState): boolean => (
  activeDecision
  && !planningRunning
  && !hidden
  && !isReadOnly
  && !wasContinued
  && actionButtonCount > 0
);

const PRIMARY_ACTION_SELECTOR = [
  '.ai-overview-action-btn--continue',
  '.ai-overview-action-btn--run',
  '.ai-overview-action-btn--build',
  '.ai-overview-action-btn--accept-srd',
  '.agent-suggestion-pill--run',
  '.agent-suggestion-pill--build',
  '.agent-suggestion-pill--action',
].join(', ');

const INELIGIBLE_ACTION_CLASSES = [
  'ai-overview-action-btn--done',
  'agent-suggestion-pill--triggered',
  'agent-suggestion-pill--locked',
];

const ATTENTION_ANIMATION_MS = 6000;

export const isAttentionEligibleButton = (
  button: HTMLButtonElement | null,
): button is HTMLButtonElement => Boolean(
  button
  && !button.disabled
  && button.getAttribute('aria-disabled') !== 'true'
  && button.getAttribute('aria-busy') !== 'true'
  && !INELIGIBLE_ACTION_CLASSES.some((className) => button.classList.contains(className)),
);

const isVisibleAttentionButton = (root: HTMLElement, button: HTMLButtonElement): boolean => {
  const rect = button.getBoundingClientRect();

  if (rect.width <= 0 || rect.height <= 0 || !root.contains(button)) return false;
  let element: HTMLElement | null = button;

  while (element) {
    const style = globalThis.getComputedStyle(element);

    if (element.hidden || element.getAttribute('aria-hidden') === 'true'
      || style.display === 'none' || style.visibility === 'hidden') return false;
    if (element === root) break;
    element = element.parentElement;
  }

  return true;
};

export const findDefaultAttentionButton = (
  root: HTMLElement,
  preferCreditRecovery = true,
): HTMLButtonElement | null => {
  if (preferCreditRecovery) {
    for (const selector of ['.ai-overview-action-btn--add-credit', '.ai-overview-action-btn--upgrade']) {
      const recovery = Array.from(root.querySelectorAll<HTMLButtonElement>(selector))
        .find((button) => isAttentionEligibleButton(button) && isVisibleAttentionButton(root, button));

      if (recovery) return recovery;
    }
  }
  const declaredPrimary = root.querySelector<HTMLButtonElement>('[data-attention-primary="true"]');

  if (declaredPrimary) return isAttentionEligibleButton(declaredPrimary) ? declaredPrimary : null;

  const knownPrimary = root.querySelector<HTMLButtonElement>(PRIMARY_ACTION_SELECTOR);

  if (knownPrimary) return isAttentionEligibleButton(knownPrimary) ? knownPrimary : null;

  const eligibleButtons = Array.from(root.querySelectorAll<HTMLButtonElement>('button'))
    .filter((button) => isAttentionEligibleButton(button)
      && !button.classList.contains('ai-overview-action-btn--share'));

  return eligibleButtons.at(-1) ?? null;
};

interface AttentionBox {
  x: number;
  y: number;
  width: number;
  height: number;
  borderRadius: string;
  targetKey: string;
  target: HTMLButtonElement;
}

const sameBox = (left: AttentionBox | null, right: AttentionBox | null): boolean => (
  left === right
  || Boolean(
    left && right
    && left.x === right.x
    && left.y === right.y
    && left.width === right.width
    && left.height === right.height
    && left.borderRadius === right.borderRadius
    && left.targetKey === right.targetKey
    && left.target === right.target,
  )
);

const measureButton = (
  root: HTMLElement,
  button: HTMLButtonElement | null,
): AttentionBox | null => {
  if (!isAttentionEligibleButton(button) || !isVisibleAttentionButton(root, button)) return null;
  const rootRect = root.getBoundingClientRect();
  const buttonRect = button.getBoundingClientRect();

  if (buttonRect.width <= 0 || buttonRect.height <= 0) return null;

  return {
    x: buttonRect.left - rootRect.left + root.scrollLeft,
    y: buttonRect.top - rootRect.top + root.scrollTop,
    width: buttonRect.width,
    height: buttonRect.height,
    borderRadius: globalThis.getComputedStyle(button).borderRadius || '6px',
    target: button,
    targetKey: button.dataset.actionKey
      || button.getAttribute('aria-label')
      || button.textContent?.trim()
      || 'action',
  };
};

/** Mounted only while idle so stale geometry cannot flash between workflow runs. */
const ActiveActionAttentionRing: React.FC = () => {
  const ringRef = useRef<HTMLDivElement>(null);
  const [{ box, animationComplete }, setAttention] = useState<{
    box: AttentionBox | null;
    animationComplete: boolean;
  }>({ box: null, animationComplete: false });
  const target = box?.target ?? null;

  useLayoutEffect(() => {
    const ring = ringRef.current;
    const root = ring?.parentElement;

    if (!ring || !root) return undefined;

    let resizeObserver: ResizeObserver | null = null;

    const refresh = () => {
      const nextTarget = findDefaultAttentionButton(root);
      const nextBox = measureButton(root, nextTarget);

      setAttention((current) => (sameBox(current.box, nextBox) ? current : {
        box: nextBox,
        animationComplete: current.box?.target === nextBox?.target && current.animationComplete,
      }));
    };

    const observeButtons = () => {
      if (typeof ResizeObserver === 'undefined') return;
      resizeObserver?.disconnect();
      resizeObserver = new ResizeObserver(refresh);
      resizeObserver.observe(root);
      root.querySelectorAll<HTMLButtonElement>('button').forEach((button) => {
        resizeObserver?.observe(button);
      });
    };

    const mutationObserver = new MutationObserver(() => {
      observeButtons();
      refresh();
    });

    globalThis.addEventListener('resize', refresh);
    mutationObserver.observe(root, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'aria-hidden', 'hidden', 'style', 'class',
        'data-attention-primary'],
      childList: true,
      subtree: true,
    });
    observeButtons();
    refresh();

    return () => {
      globalThis.removeEventListener('resize', refresh);
      mutationObserver.disconnect();
      resizeObserver?.disconnect();
    };
  }, []);

  useLayoutEffect(() => {
    const ring = ringRef.current;

    if (!ring || !target) return undefined;
    // Restart just the accents; retaining the element preserves its position glide.
    ring.style.animation = 'none';
    ring.getBoundingClientRect();
    ring.style.removeProperty('animation');
    // Reduced-motion browsers do not dispatch animationend, but still dismiss the cue.
    const timeout = globalThis.setTimeout(() => {
      setAttention((current) => ({ ...current, animationComplete: true }));
    }, ATTENTION_ANIMATION_MS);

    return () => globalThis.clearTimeout(timeout);
  }, [target]);

  return (
    <div
      ref={ringRef}
      className={`ai-overview-action-attention-ring${box && !animationComplete ? ' ai-overview-action-attention-ring--visible' : ''}`}
      style={box ? {
        width: box.width,
        height: box.height,
        borderRadius: box.borderRadius,
        transform: `translate3d(${box.x}px, ${box.y}px, 0)`,
      } : undefined}
      data-attention-target={box?.targetKey}
      onAnimationEnd={(event) => {
        if (event.target === event.currentTarget && event.animationName === 'n4') {
          setAttention((current) => ({ ...current, animationComplete: true }));
        }
      }}
      aria-hidden="true"
    />
  );
};

/** Automatic decision cue; pointer and keyboard interactions never restart it. */
export const ActionAttentionRing: React.FC<{ active: boolean }> = ({ active }) => (
  active ? <ActiveActionAttentionRing /> : null
);
